/**
 * The one module the storefront gets data from — ADR-011 §4, until `W1-T03` generates the real one.
 *
 * *"The storefront imports one module for data: the generated client from `packages/contracts`. No
 * `fetch` in a component, no hand-written URL, no Prisma anywhere near React."* `W1-T03` (zod →
 * OpenAPI → typed client) has not run, so this is the stand-in: **one** module, hand-written URLs
 * confined to it, and every response parsed by the contract's own zod schema on the way in.
 *
 * That last part is what keeps it honest. A hand-written client that trusts the wire is a second
 * definition of the API shape; one that parses is a *consumer* of the single definition, and it
 * fails loudly the day the endpoint drifts. When `W1-T03` lands, the generated client replaces the
 * calls below and nothing else in the app changes — the query keys and the loaders stay.
 *
 * Not a route module, so no route rule applies here. But `axios` is created **per client instance**
 * rather than as a module singleton, for the same reason R6 exists: on a Worker a module-scope
 * client outlives the request that configured it.
 */
import {
  AskPricesSchema,
  AuctionBidListSchema,
  AuctionPublishedSchema,
  AuctionSchema,
  BookingPaymentSchema,
  BookingSchema,
  CalloutReleaseViewSchema,
  MoneyDocumentPageSchema,
  MoneyDocumentSchema,
  BidListSchema,
  BidSchema,
  CategoryListSchema,
  ClientProfileSchema,
  HandshakeIssuedSchema,
  HandshakeRedeemedSchema,
  HandshakeStateSchema,
  JobAskSchema,
  JobSchema,
  ProviderProfileOwnSchema,
  ProviderProfileSchema,
  JobFeedPageSchema,
  JobMessageSchema,
  JobThreadListSchema,
  JobThreadViewSchema,
  PayoutOnboardingLinkSchema,
  PayoutStatusSchema,
  ProviderSubscriptionSchema,
  HostedRedirectSchema,
  SubscriptionPlansSchema,
  QuotePageSchema,
  QuoteSchema,
  SavedAddressEntrySchema,
  SavedAddressListSchema,
  SavedAddressSchema,
  SearchResponseSchema,
  type AskPrices,
  type Auction,
  type AuctionBid,
  type AuctionPublishInput,
  type AuctionPublished,
  type Booking,
  type CalloutReleaseView,
  type MoneyDocument,
  type MoneyDocumentPage,
  type BookingPayment,
  type Bid,
  type BidInput,
  type CategorySummary,
  type ClientProfile,
  type ClientProfileUpdate,
  type HandshakeIssued,
  type HandshakeRedeemInput,
  type HandshakeRedeemed,
  type HandshakeState,
  type Job,
  type JobAsk,
  type JobAddressInput,
  type JobDraftInput,
  type JobFeedPage,
  type JobMessage,
  type JobThreadList,
  type JobThreadView,
  type PayoutOnboardingLink,
  type PayoutOnboardingLinkInput,
  type PayoutStatus,
  type HostedRedirect,
  type ProviderSubscription,
  type PublishCheckoutInput,
  type SubscriptionCheckoutInput,
  type SubscriptionPlanInput,
  type SubscriptionPlans,
  type ProviderProfile,
  type ProviderProfileCreate,
  type ProviderProfileOwn,
  type ProviderProfileUpdate,
  type Quote,
  type QuoteInput,
  type QuotePage,
  type SavedAddressCreate,
  type SavedAddressEntry,
  type SearchResponse,
} from '@marketplace/contracts';
import * as z from 'zod';
import { type SearchQuery } from '@marketplace/ui';
import axios, { type AxiosInstance } from 'axios';
import { SessionSchema, SessionUserSchema, type SessionUser } from './session.js';

/**
 * `ApiError` lives in its own module (`api-error.ts`) and is re-exported here so that every caller
 * keeps one import. Moving it broke a runtime import cycle — see that file.
 */
import { ApiError } from './api-error.js';

export { ApiError } from './api-error.js';

export interface ApiClient {
  getCategories: (locale: string) => Promise<CategorySummary[]>;
  /**
   * Takes the query as **strings** — the shape that goes in a URL — not `SearchQuerySchema`'s parsed
   * output. That output is transformed: `cursor` comes out as a decoded `CursorPosition` and `sort`
   * as an array, so serialising it back would need the contract's encoders and would re-derive a
   * string the caller already had. The caller validates, then sends what the URL said.
   */
  search: (query: SearchQuery, locale: string) => Promise<SearchResponse>;
  /**
   * The id is a uuid the caller has **already** checked against `ProviderIdSchema`. This method does
   * not re-check it: a client that validates its own arguments invites a caller to stop, and the
   * caller is the one route that can render a 404 instead of sending a request nobody should send.
   */
  getProvider: (id: string, locale: string) => Promise<ProviderProfile>;

  /**
   * ── The client's own jobs, and the quotes on them — `W4-T04` §3.2 ───────────────────────────
   *
   * The first calls in this client that need a **session** rather than a locale. They carry no
   * `Accept-Language`: a job's text is what its owner typed, and a quote's is what a professional
   * wrote. Neither is ours to translate, and asking for a language we cannot honour is a header
   * that reads as a promise.
   */
  getMyJobs: () => Promise<Job[]>;
  getJob: (id: string) => Promise<Job>;
  /** One page. `cursor` continues the previous one — the screen appends and re-ranks (§3.3). */
  getJobQuotes: (jobId: string, cursor?: string) => Promise<QuotePage>;
  /**
   * The provider's side — `W4-T07`. One page of the market, `cursor` continuing the previous one.
   *
   * No locale header, like the two calls above and for the same reason: a job's title is what a
   * client typed. The **coverage labels** are the one thing here that has two spellings, and they
   * arrive as `nameEs`/`nameEn` on the row rather than resolved by the endpoint — so the screen picks,
   * and asking for a language would be a header the API cannot honour.
   */
  getJobFeed: (cursor?: string) => Promise<JobFeedPage>;
  /**
   * Accept or reject, as one call taking the decision.
   *
   * One method rather than two, mirroring the permission: the API guards both with
   * `quote:decide-for-own-job` because they are one capability, and a client that split them would
   * be the only place in the stack that thought otherwise.
   */
  decideQuote: (quoteId: string, decision: 'accept' | 'reject') => Promise<Quote>;
  /**
   * `W4-T17` — take an acceptance back, before anything is paid. A refusal reaches the page as its
   * code: `QUOTE_PAYMENT_PENDING` while a payment for the quote is in progress, `CONFLICT` otherwise.
   */
  unacceptQuote: (quoteId: string) => Promise<Quote>;

  /**
   * ── The handshake — `W4-T09` §4 ───────────────────────────────────────────────────────────────
   *
   * The professional's own quotes are what `/work` lists: a quote reads `AWARDED` once its call-out
   * fee settled (`W4-T05`), which is exactly a job that is `AWARDED` or `IN_PROGRESS`. Issuing takes
   * no body and answers the only shape that carries the token and the short code in clear; the state
   * either party polls is strict, so an answer that carried either fails here. A redeem sends one
   * form or the other in the **body** — never a URL, which a server would log.
   */
  getMyQuotes: () => Promise<Quote[]>;
  issueHandshake: (jobId: string) => Promise<HandshakeIssued>;
  getHandshakeState: (jobId: string) => Promise<HandshakeState>;
  redeemHandshake: (input: HandshakeRedeemInput) => Promise<HandshakeRedeemed>;

  /**
   * ── The call-out payment — `W5-T02` §4.1, §4.2 ──────────────────────────────────────────────
   *
   * Booking a quote takes no body: the amount is the quote's (AC 4). The answer carries the intent's
   * client secret and the publishable key the Payment Element confirms with. The read is what the
   * 3DS return page polls; it has no secret in it.
   */
  createBooking: (quoteId: string) => Promise<BookingPayment>;
  getBooking: (bookingId: string) => Promise<Booking>;

  /**
   * ── The funnel's two writes — `W4-T10` §2.1, §2.5 ──────────────────────────────────────────
   *
   * Posting is two calls rather than one because the API is two endpoints: a draft, then the
   * transition. The route action makes both, and keeps the draft when the second one fails.
   */
  createJob: (input: JobDraftInput) => Promise<Job>;
  /**
   * `W4-T11` §3.5 — the address the client asked to remember, or `null` when they never have. What
   * *"usar mi dirección guardada"* fills the job form with.
   */
  getSavedAddress: () => Promise<JobAddressInput | null>;
  publishJob: (id: string) => Promise<Job>;
  createQuote: (jobId: string, input: QuoteInput) => Promise<Quote>;

  /**
   * ── Auctions — `W6-T06` ──────────────────────────────────────────────────────────────────────
   *
   * The client publishes a draft as an auction, reads it, reads the bids once it has closed, and
   * awards one. The provider places, lowers and withdraws bids and reads their own. Sealed: nothing
   * here lets either side read the other's bids before close — the API offers no such call.
   */
  publishAuction: (jobId: string, input: AuctionPublishInput) => Promise<AuctionPublished>;
  getJobAuction: (jobId: string) => Promise<Auction>;
  getAuctionBids: (jobId: string) => Promise<AuctionBid[]>;
  awardAuction: (jobId: string, bidId: string) => Promise<Auction>;
  placeBid: (jobId: string, input: BidInput) => Promise<Bid>;
  withdrawBid: (bidId: string) => Promise<Bid>;
  getMyBids: () => Promise<Bid[]>;
  /** `W6-T08`: the provider's own bids on one job — what the bid panel reads. */
  getMyBidsForJob: (jobId: string) => Promise<Bid[]>;

  /**
   * ── A job's conversations — `W4-T06` §3.3 ──────────────────────────────────────────────────
   *
   * Two sides, like the API. The provider has one thread per job, so their calls name the job; the
   * client has one per provider who wrote, so theirs name the thread. No locale header: a message
   * is what a person typed.
   */
  getMyThread: (jobId: string) => Promise<JobThreadView>;
  sendMyThreadMessage: (jobId: string, body: string) => Promise<JobMessage>;
  getJobThreads: (jobId: string) => Promise<JobThreadList>;
  getJobThread: (jobId: string, threadId: string) => Promise<JobThreadView>;
  sendJobThreadMessage: (jobId: string, threadId: string, body: string) => Promise<JobMessage>;

  /**
   * ── Becoming a provider — `W2-T05` §3.5 ────────────────────────────────────────────────────
   *
   * `null` is "no provider profile yet" — the API's `404` on `/providers/me`, which for this page is
   * a state (show the form) rather than a failure. Creating answers the public projection, as `PUT`
   * does; the page only needs to know it worked.
   */
  getMyProviderProfile: () => Promise<ProviderProfileOwn | null>;
  createProviderProfile: (input: ProviderProfileCreate) => Promise<ProviderProfile>;
  /** `W2-T05` §7 — the whole document, without coordinates: the server places the postcode. */
  updateProviderProfile: (input: ProviderProfileUpdate) => Promise<ProviderProfile>;

  /**
   * ── A provider's payouts — `W5-T01` §4.1, §4.2 ─────────────────────────────────────────────
   *
   * The status is our own table's answer and never Stripe's: the schema has no key a Stripe id could
   * ride in, so an answer carrying one fails here. The link names where the provider came from, never
   * a URL — the return and refresh URLs are the server's.
   */
  getMyPayouts: () => Promise<PayoutStatus>;
  createPayoutOnboardingLink: (
    from: PayoutOnboardingLinkInput['from'],
  ) => Promise<PayoutOnboardingLink>;
  /**
   * `W5-T18` §4.7 — where the professional's share of a started call-out stands, read by job. A view
   * of our own row: `CalloutReleaseViewSchema` is strict, so an answer carrying a Stripe id fails here.
   */
  getCalloutRelease: (jobId: string) => Promise<CalloutReleaseView>;
  /**
   * `W5-T09` §4.4, §4.5 — the caller's provisional receipts and payout statements, newest first, and
   * one of them. A document is its recipient's: anybody else's is the same `404` as none. The PDF is
   * not read here: the page links to `/api/documents/{id}/pdf` and the browser downloads it.
   */
  getMyDocuments: (query?: {
    bookingId?: string | undefined;
    cursor?: string | undefined;
  }) => Promise<MoneyDocumentPage>;
  getDocument: (id: string) => Promise<MoneyDocument>;

  /**
   * ── A provider's subscription — `W5-T07` §4.1–§4.5 ─────────────────────────────────────────
   *
   * Our table's answer, never Stripe's: `ProviderSubscriptionSchema` has no key a Stripe id could ride
   * in. The plans carry integer cents and the tax rate in basis points, so nothing on screen divides
   * money. Checkout names where the provider came from and a switch names the kind, never a URL or a
   * price: both are the server's. Checkout and the portal answer a Stripe-hosted page to go to.
   */
  getMySubscription: () => Promise<ProviderSubscription>;
  getSubscriptionPlans: () => Promise<SubscriptionPlans>;
  startSubscriptionCheckout: (from: SubscriptionCheckoutInput['from']) => Promise<HostedRedirect>;
  changeSubscriptionPlan: (kind: SubscriptionPlanInput['kind']) => Promise<ProviderSubscription>;
  /** No body: the portal is the caller's own customer's, and the return URL is the server's. */
  openBillingPortal: () => Promise<HostedRedirect>;

  /**
   * ── Charge to ask — `W5-T12` §4.1–§4.4 ─────────────────────────────────────────────────────
   *
   * What asking costs, and what the owner paid for on one job: integer cents and counts, never a
   * Stripe id and never a provider (the schemas have no key for either). The two checkouts name a
   * mode, or nothing at all, and answer the Stripe-hosted page to go to; the price and the return
   * URLs are the server's.
   */
  getAskPrices: () => Promise<AskPrices>;
  getJobAsk: (jobId: string) => Promise<JobAsk>;
  startPublishCheckout: (jobId: string, input: PublishCheckoutInput) => Promise<HostedRedirect>;
  /** No body (§4.4): which window and what it costs are the server's. */
  startQuoteWindowCheckout: (jobId: string) => Promise<HostedRedirect>;

  /**
   * ── A client's profile and address book — `W2-T04` §4 ─────────────────────────────────────
   *
   * Lines, never a point: the server places the postcode. An entry's id is its address row's, and
   * an edit answers a **new** one (§3.3), so a caller keys nothing on an id across a save.
   */
  getMyClientProfile: () => Promise<ClientProfile>;
  updateMyClientProfile: (input: ClientProfileUpdate) => Promise<ClientProfile>;
  getMyAddresses: () => Promise<SavedAddressEntry[]>;
  createMyAddress: (input: SavedAddressCreate) => Promise<SavedAddressEntry>;
  updateMyAddress: (id: string, input: SavedAddressCreate) => Promise<SavedAddressEntry>;
  deleteMyAddress: (id: string) => Promise<void>;
  setMyDefaultAddress: (id: string) => Promise<ClientProfile>;

  /**
   * ── The auth calls — `W2-T09` §4.3 ───────────────────────────────────────────────────────────
   *
   * Here rather than behind better-auth's client SDK, because ADR-011 §4 asks for exactly one
   * module the storefront gets data from and this one already has the properties these calls need:
   * same origin by default (which is what makes the `SameSite=Lax` cookie work through the dev
   * proxy and through `W0-T28` in preview), one place that turns a transport failure into
   * `ApiError`, and one file to replace when `W1-T03` generates the real client. A second HTTP
   * client for five endpoints would put the session cookie on a code path no other call uses — and
   * the cookie is the part most likely to break.
   *
   * The paths are better-auth 1.7.4's own, read from the installed package.
   */

  /**
   * Returns **nothing**, and that is the point.
   *
   * `W2-T01` §4.5 answers a duplicate signup with a synthetic `200` — a user object with
   * `roles: null` and no row written — so that the endpoint cannot be used to discover which
   * addresses are registered. Handing that body back to a page puts the enumeration oracle one
   * `if` away from the screen. The client sees the request succeeded; nothing else.
   */
  signUp: (input: {
    name: string;
    email: string;
    password: string;
    /** Where the emailed link should land. Relative, so `trustedOrigins` needs no entry. */
    callbackURL: string;
  }) => Promise<void>;
  /**
   * Returns the user the API just authenticated — **the server's own answer, not a guess.**
   *
   * better-auth's sign-in response carries the whole user, so asking `get-session` immediately
   * afterwards is a second request for something we were just told. The caller seeds the session
   * cache with this, which is what makes signing in **one** API call.
   */
  signIn: (input: { email: string; password: string }) => Promise<SessionUser>;
  signOut: () => Promise<void>;
  /** `null` for a visitor with no session — better-auth answers `200` with a null body, not `401`. */
  getSession: () => Promise<SessionUser | null>;
  /** Works without a session, is floored at 500 ms, and answers `200` for an address it cannot find. */
  resendVerification: (input: { email: string; callbackURL: string }) => Promise<void>;
  requestPasswordReset: (input: { email: string; redirectTo: string }) => Promise<void>;
  resetPassword: (input: { token: string; newPassword: string }) => Promise<void>;
}

/**
 * Same origin by default, which is what the MSW handlers intercept and what a single-origin
 * deployment wants. `VITE_API_URL` exists for the day the API moves to its own host.
 */
function baseUrl(): string {
  const configured: unknown = import.meta.env['VITE_API_URL'];
  return typeof configured === 'string' && configured !== '' ? configured : '/';
}

/**
 * **Every path in this file starts with `api/`, and `W0-T28` is why.**
 *
 * One origin means the storefront and the API share a hostname, and the edge decides which is
 * which by the path: `/api/*` is forwarded to the API and everything else is the application. A
 * call to `categories` would therefore be answered by the SPA's own `index.html` — a 200 full of
 * HTML, which fails as a JSON parse error somewhere far from the cause.
 *
 * It costs nothing today: better-auth already lives under `/api/auth`, and the three endpoints
 * below do not exist yet (`W3-T01`, `W3-T05`, `W3-T07`) — the MSW handlers that stand in for them
 * match on a wildcard prefix. Which is exactly why the prefix goes in now rather than when the
 * first of them ships and breaks only when deployed.
 */

/**
 * The machine-readable code a failed response carries, in whichever of the two shapes it arrives.
 *
 * Our own API answers `{ error: { code, … } }` (README, `apps/api/src/lib/errors.ts`); better-auth
 * answers `{ code, message }`. Both are *codes*, and a code is the only field a client may branch
 * on — `403 EMAIL_NOT_VERIFIED` has to be distinguishable from the `403` an origin check produces,
 * or the login page tells a user to check their inbox because a proxy header was wrong.
 *
 * The status alone is not enough, and the human message is not stable enough. This is the one place
 * that knows either shape exists.
 */
function errorCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const body = data as { code?: unknown; error?: { code?: unknown } };
  if (typeof body.code === 'string') return body.code;
  if (typeof body.error?.code === 'string') return body.error.code;
  return undefined;
}

/**
 * `W2-T07`: better-auth's limiter answers `429` with `X-Retry-After: <seconds>`. A whole number of
 * seconds, at least one — anything else is treated as absent rather than shown to a person.
 */
function retryAfter(headers: unknown): number | undefined {
  const value = (headers as Record<string, unknown> | undefined)?.['x-retry-after'];
  const seconds = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN;
  return Number.isSafeInteger(seconds) && seconds >= 1 ? seconds : undefined;
}

/** Every call goes through here, so there is one place that turns a transport error into `ApiError`. */
async function call<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (error) {
    if (axios.isAxiosError(error)) {
      // The code when there is one, axios's own sentence when there is not — a request that never
      // got an answer has no code, and inventing one would be claiming to know what the server did.
      const wait = retryAfter(error.response?.headers);
      throw new ApiError(error.response?.status, errorCode(error.response?.data) ?? error.message, {
        cause: error,
        ...(wait === undefined ? {} : { retryAfter: wait }),
      });
    }
    throw error;
  }
}

export function createApiClient(
  http: AxiosInstance = axios.create({ baseURL: baseUrl() }),
): ApiClient {
  return {
    async getCategories(locale) {
      const response = await http.get<unknown>('api/categories', {
        // The endpoint resolves `nameEs`/`nameEn` down to one `name`, so it has to be told which.
        headers: { 'Accept-Language': locale },
      });
      return CategoryListSchema.parse(response.data).items;
    },

    async search(query, locale) {
      const params = new URLSearchParams(Object.entries(query));
      const response = await http.get<unknown>(`api/search?${params.toString()}`, {
        headers: { 'Accept-Language': locale },
      });
      // Parsed on the way in, like the categories call. A client that trusts the wire is a second
      // definition of the API shape; one that parses is a consumer of the single definition.
      return SearchResponseSchema.parse(response.data);
    },

    async getProvider(id, locale) {
      const response = await call(() =>
        http.get<unknown>(`api/providers/${id}`, { headers: { 'Accept-Language': locale } }),
      );
      return ProviderProfileSchema.parse(response.data);
    },

    async getMyJobs() {
      const response = await call(() => http.get<unknown>('api/me/jobs'));
      // The envelope is one line here and the *item* is the contract's — `GET /api/me/jobs` is the
      // last uncursored list in this slice (`W4-T04` §2.7) and has no page schema to import.
      return z.object({ items: z.array(JobSchema) }).parse(response.data).items;
    },

    async getJob(id) {
      const response = await call(() => http.get<unknown>(`api/jobs/${id}`));
      return JobSchema.parse(response.data);
    },

    async getJobQuotes(jobId, cursor) {
      const query = cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`;
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/quotes${query}`));
      return QuotePageSchema.parse(response.data);
    },

    async getJobFeed(cursor) {
      const query = cursor === undefined ? '' : `?cursor=${encodeURIComponent(cursor)}`;
      const response = await call(() => http.get<unknown>(`api/me/job-feed${query}`));
      return JobFeedPageSchema.parse(response.data);
    },

    async createBooking(quoteId) {
      const response = await call(() => http.post<unknown>(`api/quotes/${quoteId}/booking`, {}));
      return BookingPaymentSchema.parse(response.data);
    },

    async getBooking(bookingId) {
      const response = await call(() => http.get<unknown>(`api/bookings/${bookingId}`));
      return BookingSchema.parse(response.data);
    },

    async decideQuote(quoteId, decision) {
      const response = await call(() =>
        http.post<unknown>(`api/quotes/${quoteId}/${decision}`, {}),
      );
      return QuoteSchema.parse(response.data);
    },

    async unacceptQuote(quoteId) {
      const response = await call(() => http.post<unknown>(`api/quotes/${quoteId}/unaccept`, {}));
      return QuoteSchema.parse(response.data);
    },

    async getMyQuotes() {
      const response = await call(() => http.get<unknown>('api/me/quotes'));
      // The envelope is one line, as `getMyJobs`' is: `/me/quotes` has no page schema to import.
      return z.object({ items: z.array(QuoteSchema) }).parse(response.data).items;
    },

    async issueHandshake(jobId) {
      // `{}` for the reason `publishJob` gives.
      const response = await call(() => http.post<unknown>(`api/jobs/${jobId}/handshake`, {}));
      return HandshakeIssuedSchema.parse(response.data);
    },

    async getHandshakeState(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/handshake`));
      return HandshakeStateSchema.parse(response.data);
    },

    async redeemHandshake(input) {
      const response = await call(() => http.post<unknown>('api/handshake/redeem', input));
      return HandshakeRedeemedSchema.parse(response.data);
    },

    async createJob(input) {
      const response = await call(() => http.post<unknown>('api/jobs', input));
      return JobSchema.parse(response.data);
    },

    async getSavedAddress() {
      const response = await call(() => http.get<unknown>('api/clients/me/address'));
      return SavedAddressSchema.parse(response.data).address;
    },

    async publishJob(id) {
      // `{}` for the reason `signOut` gives: a JSON `POST` with an empty body is a `400`.
      const response = await call(() => http.post<unknown>(`api/jobs/${id}/publish`, {}));
      return JobSchema.parse(response.data);
    },

    async createQuote(jobId, input) {
      const response = await call(() => http.post<unknown>(`api/jobs/${jobId}/quotes`, input));
      return QuoteSchema.parse(response.data);
    },

    async publishAuction(jobId, input) {
      const response = await call(() => http.post<unknown>(`api/jobs/${jobId}/auction`, input));
      return AuctionPublishedSchema.parse(response.data);
    },

    async getJobAuction(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/auction`));
      return AuctionSchema.parse(response.data);
    },

    async getAuctionBids(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/auction/bids`));
      return AuctionBidListSchema.parse(response.data).items;
    },

    async awardAuction(jobId, bidId) {
      const response = await call(() =>
        http.post<unknown>(`api/jobs/${jobId}/auction/award`, { bidId }),
      );
      return AuctionSchema.parse(response.data);
    },

    async placeBid(jobId, input) {
      const response = await call(() => http.post<unknown>(`api/jobs/${jobId}/bids`, input));
      return BidSchema.parse(response.data);
    },

    async withdrawBid(bidId) {
      // `{}` for the reason `publishJob` gives.
      const response = await call(() => http.post<unknown>(`api/bids/${bidId}/withdraw`, {}));
      return BidSchema.parse(response.data);
    },

    async getMyBids() {
      const response = await call(() => http.get<unknown>('api/me/bids'));
      return BidListSchema.parse(response.data).items;
    },

    async getMyBidsForJob(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/bids/mine`));
      return BidListSchema.parse(response.data).items;
    },

    async getMyThread(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/thread`));
      return JobThreadViewSchema.parse(response.data);
    },

    async sendMyThreadMessage(jobId, body) {
      const response = await call(() =>
        http.post<unknown>(`api/jobs/${jobId}/thread/messages`, { body }),
      );
      return JobMessageSchema.parse(response.data);
    },

    async getJobThreads(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/threads`));
      return JobThreadListSchema.parse(response.data);
    },

    async getJobThread(jobId, threadId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/threads/${threadId}`));
      return JobThreadViewSchema.parse(response.data);
    },

    async sendJobThreadMessage(jobId, threadId, body) {
      const response = await call(() =>
        http.post<unknown>(`api/jobs/${jobId}/threads/${threadId}/messages`, { body }),
      );
      return JobMessageSchema.parse(response.data);
    },

    async getMyProviderProfile() {
      try {
        const response = await call(() => http.get<unknown>('api/providers/me'));
        return ProviderProfileOwnSchema.parse(response.data);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },

    async createProviderProfile(input) {
      const response = await call(() => http.post<unknown>('api/providers/me', input));
      return ProviderProfileSchema.parse(response.data);
    },

    async updateProviderProfile(input) {
      const response = await call(() => http.put<unknown>('api/providers/me', input));
      return ProviderProfileSchema.parse(response.data);
    },

    async getMyPayouts() {
      const response = await call(() => http.get<unknown>('api/me/payouts'));
      return PayoutStatusSchema.parse(response.data);
    },

    async createPayoutOnboardingLink(from) {
      const response = await call(() =>
        http.post<unknown>('api/me/payouts/onboarding-link', { from }),
      );
      return PayoutOnboardingLinkSchema.parse(response.data);
    },

    async getCalloutRelease(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/release`));
      return CalloutReleaseViewSchema.parse(response.data);
    },

    async getMyDocuments(query = {}) {
      const params = new URLSearchParams(
        Object.entries(query).filter((entry): entry is [string, string] => entry[1] !== undefined),
      );
      const search = params.size === 0 ? '' : `?${params.toString()}`;
      const response = await call(() => http.get<unknown>(`api/me/documents${search}`));
      return MoneyDocumentPageSchema.parse(response.data);
    },

    async getDocument(id) {
      const response = await call(() => http.get<unknown>(`api/documents/${id}`));
      return MoneyDocumentSchema.parse(response.data);
    },

    async getMySubscription() {
      const response = await call(() => http.get<unknown>('api/me/subscription'));
      return ProviderSubscriptionSchema.parse(response.data);
    },

    async getSubscriptionPlans() {
      const response = await call(() => http.get<unknown>('api/subscription/plans'));
      return SubscriptionPlansSchema.parse(response.data);
    },

    async startSubscriptionCheckout(from) {
      const response = await call(() =>
        http.post<unknown>('api/me/subscription/checkout', { from }),
      );
      return HostedRedirectSchema.parse(response.data);
    },

    async changeSubscriptionPlan(kind) {
      const response = await call(() => http.post<unknown>('api/me/subscription/plan', { kind }));
      return ProviderSubscriptionSchema.parse(response.data);
    },

    async openBillingPortal() {
      const response = await call(() => http.post<unknown>('api/me/subscription/portal'));
      return HostedRedirectSchema.parse(response.data);
    },

    async getAskPrices() {
      const response = await call(() => http.get<unknown>('api/ask/prices'));
      return AskPricesSchema.parse(response.data);
    },

    async getJobAsk(jobId) {
      const response = await call(() => http.get<unknown>(`api/jobs/${jobId}/ask`));
      return JobAskSchema.parse(response.data);
    },

    async startPublishCheckout(jobId, input) {
      const response = await call(() =>
        http.post<unknown>(`api/jobs/${jobId}/publish/checkout`, input),
      );
      return HostedRedirectSchema.parse(response.data);
    },

    async startQuoteWindowCheckout(jobId) {
      // No body, as `openBillingPortal`: the route refuses one with keys, and takes none (§4.4).
      const response = await call(() =>
        http.post<unknown>(`api/jobs/${jobId}/quote-windows/checkout`),
      );
      return HostedRedirectSchema.parse(response.data);
    },

    async getMyClientProfile() {
      const response = await call(() => http.get<unknown>('api/clients/me'));
      return ClientProfileSchema.parse(response.data);
    },

    async updateMyClientProfile(input) {
      const response = await call(() => http.put<unknown>('api/clients/me', input));
      return ClientProfileSchema.parse(response.data);
    },

    async getMyAddresses() {
      const response = await call(() => http.get<unknown>('api/clients/me/addresses'));
      return SavedAddressListSchema.parse(response.data).addresses;
    },

    async createMyAddress(input) {
      const response = await call(() => http.post<unknown>('api/clients/me/addresses', input));
      return SavedAddressEntrySchema.parse(response.data);
    },

    async updateMyAddress(id, input) {
      const response = await call(() =>
        http.put<unknown>(`api/clients/me/addresses/${encodeURIComponent(id)}`, input),
      );
      return SavedAddressEntrySchema.parse(response.data);
    },

    async deleteMyAddress(id) {
      await call(() => http.delete(`api/clients/me/addresses/${encodeURIComponent(id)}`));
    },

    async setMyDefaultAddress(id) {
      const response = await call(() =>
        http.put<unknown>('api/clients/me/default-address', { addressId: id }),
      );
      return ClientProfileSchema.parse(response.data);
    },

    async signUp(input) {
      // The response is awaited and dropped. See the interface: a client that returns it is a page
      // that can tell a duplicate from a new account.
      await call(() => http.post<unknown>('api/auth/sign-up/email', input));
    },

    async signIn(input) {
      const response = await call(() => http.post<unknown>('api/auth/sign-in/email', input));
      // Parsed on the way in like every other response: a client that trusts the wire is a second
      // definition of the shape. `SessionUserSchema` takes the four fields the storefront uses and
      // drops the rest — `roles` included, because `W2-T03` owns what a role may change.
      return SessionUserSchema.parse((response.data as { user?: unknown }).user);
    },

    async signOut() {
      /**
       * `{}` rather than no body, and it is not a formality — measured against the running API.
       *
       * Sign-out is the one auth route with nothing to send, and a `POST` that announces
       * `application/json` with an empty body is rejected by Fastify's own JSON parser before
       * better-auth sees it: `400 VALIDATION_FAILED, "Body cannot be empty when content-type is set
       * to 'application/json'"`. (`plugins/auth.ts`'s pass-through parser is registered as `*`,
       * which Fastify uses only for content types that have no parser of their own — so JSON still
       * goes through the built-in one.) An empty object costs two bytes and does not depend on
       * whether the HTTP client felt like sending a content-type header.
       */
      await call(() => http.post<unknown>('api/auth/sign-out', {}));
    },

    async getSession() {
      const response = await call(() => http.get<unknown>('api/auth/get-session'));
      // `.catch(null)` in the schema, not a `safeParse` here: a session body we cannot read is a
      // visitor we cannot identify, which is the signed-out rendering — never a thrown loader.
      return SessionSchema.parse(response.data)?.user ?? null;
    },

    async resendVerification(input) {
      await call(() => http.post<unknown>('api/auth/send-verification-email', input));
    },

    async requestPasswordReset(input) {
      await call(() => http.post<unknown>('api/auth/request-password-reset', input));
    },

    async resetPassword(input) {
      await call(() => http.post<unknown>('api/auth/reset-password', input));
    },
  };
}
